import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import { PoundSterling, MessageCircle, LogOut, Copy, Check, MapPin, Clock, Loader2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import EndSessionModal from "../components/EndSessionModal";

const API_BASE_URL = "https://qwipsvc.up.railway.app";

interface SessionData {
  start_dt: string;
  end_dt: string | null;
  access_code: number | null;
}

interface PodData {
  name: string;
  address: string;
  price_per_minute: number;
}

interface ApiResponse {
  session_data: SessionData;
  pod_data: PodData;
}

// Simple JWT payload decoder (no verification - just for reading claims)
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

// Helper to pad numbers with leading zeros
const pad2 = (n: number): string => n.toString().padStart(2, "0");

export default function ActiveSession() {
  const [searchParams] = useSearchParams();
  const [copied, setCopied] = useState(false);
  const [showEndSessionModal, setShowEndSessionModal] = useState(false);
  
  // API state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [podData, setPodData] = useState<PodData | null>(null);
  
  // Timer state for active sessions
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const timerRef = useRef<number | null>(null);

  const sessionToken = searchParams.get("t") || "";
  
  // Extract session_id from JWT for rating storage
  const sessionId = useMemo(() => {
    if (!sessionToken) return undefined;
    const payload = decodeJwtPayload(sessionToken);
    // Try common claim names for session ID
    return (payload?.session_id || payload?.sub || payload?.sid) as string | undefined;
  }, [sessionToken]);

  // Calculate final cost for ended sessions
  const calculateFinalCost = useCallback((session: SessionData, pod: PodData): string | null => {
    if (!session.end_dt) return null;
    
    const startDate = new Date(session.start_dt);
    const endDate = new Date(session.end_dt);
    
    // Guard against invalid dates
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return null; // Will trigger error display
    }
    
    const durationMinutes = (endDate.getTime() - startDate.getTime()) / 60000;
    const totalCost = durationMinutes * pod.price_per_minute;
    
    return `£${Math.max(0, totalCost).toFixed(2)}`;
  }, []);

  // Format elapsed seconds as HH:MM:SS
  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${pad2(hours)}:${pad2(minutes)}:${pad2(secs)}`;
  };

  const fetchSessionData = useCallback(async () => {
    if (!sessionToken) {
      setError("Missing session token");
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/get-session-data`, {
        headers: {
          "Authorization": `Bearer ${sessionToken}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch session data");
      }

      const data: ApiResponse = await response.json();
      
      // Validate dates
      const startDate = new Date(data.session_data.start_dt);
      if (isNaN(startDate.getTime())) {
        throw new Error("Invalid start date");
      }
      
      if (data.session_data.end_dt) {
        const endDate = new Date(data.session_data.end_dt);
        if (isNaN(endDate.getTime())) {
          throw new Error("Invalid end date");
        }
      }
      
      setSessionData(data.session_data);
      setPodData(data.pod_data);
      
      // If session is active, calculate initial elapsed time
      if (!data.session_data.end_dt) {
        const initialElapsed = Math.floor((Date.now() - startDate.getTime()) / 1000);
        setElapsedSeconds(Math.max(0, initialElapsed));
      }
      
      setError(null);
    } catch (err) {
      setError("Failed to load session");
    } finally {
      setIsLoading(false);
    }
  }, [sessionToken]);

  // Fetch data on mount and when token changes
  useEffect(() => {
    // Reset state when token changes
    setIsLoading(true);
    setError(null);
    setSessionData(null);
    setPodData(null);
    setElapsedSeconds(0);
    
    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    fetchSessionData();
  }, [fetchSessionData]);

  // Timer effect for active sessions
  useEffect(() => {
    // Only run timer if session is active (no end_dt)
    if (!sessionData || sessionData.end_dt !== null) {
      return;
    }
    
    // Start the interval
    timerRef.current = window.setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);
    
    // Cleanup on unmount or when session ends
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [sessionData]);

  const getAccessCodeString = (): string => {
    if (!sessionData) return "0000";
    return String(sessionData.access_code).padStart(4, "0");
  };

  const handleCopyCode = () => {
    const code = getAccessCodeString();
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Access code copied!");
    setTimeout(() => setCopied(false), 2000);
  };


  const handleEndSession = () => {
    setShowEndSessionModal(true);
  };

  const isSessionEnded = sessionData?.end_dt !== null && sessionData?.end_dt !== undefined;
  
  // Calculate final cost for ended sessions
  const finalCost = sessionData && podData && isSessionEnded 
    ? calculateFinalCost(sessionData, podData) 
    : null;

  // Error state - including date parsing failures
  if (!sessionToken || error || (isSessionEnded && finalCost === null)) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
        <div className="border-2 border-destructive rounded-xl p-6 max-w-md w-full text-center">
          <p className="text-destructive font-medium">
            Error – contact support for more details.
          </p>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="bg-primary text-primary-foreground px-6 py-4">
        <div className="flex items-center justify-between">
          <span className="text-lg font-semibold tracking-tight">qwip</span>
          <span className="text-sm opacity-80">
            {isSessionEnded ? 'Session Ended' : 'Session Active'}
          </span>
        </div>
      </div>

      <div className="p-6 space-y-6 max-w-md mx-auto">
        {/* Pod Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card rounded-2xl p-6 border border-border"
        >
          <h1 className="text-2xl font-bold text-foreground mb-2">
            {podData?.name}
          </h1>
          <div className="flex items-center gap-2 text-muted-foreground">
            <MapPin className="w-4 h-4" />
            <span className="text-sm">{podData?.address}</span>
          </div>
        </motion.div>

        {/* Session Cost/Duration Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-card rounded-2xl p-6 border border-border"
        >
          {isSessionEnded ? (
            /* Ended session: show final cost */
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <PoundSterling className="w-5 h-5 text-primary" />
                </div>
                <span className="text-muted-foreground font-medium">Final session cost</span>
              </div>
              <div className="text-4xl font-bold text-foreground">
                {finalCost}
              </div>
            </>
          ) : (
            /* Active session: show live duration timer */
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-primary" />
                </div>
                <span className="text-muted-foreground font-medium">Session duration</span>
              </div>
              <div className="text-4xl font-bold text-foreground font-mono">
                {formatDuration(elapsedSeconds)}
              </div>
            </>
          )}
        </motion.div>

        {/* Access Code Card - only render if access_code is present */}
        {sessionData?.access_code !== null && sessionData?.access_code !== undefined && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card rounded-2xl p-6 border border-border"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-muted-foreground font-medium">Access Code</span>
              <button
                onClick={handleCopyCode}
                className="flex items-center gap-2 text-primary text-sm font-medium"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy
                  </>
                )}
              </button>
            </div>
            <div className="flex justify-center gap-3">
              {getAccessCodeString().split("").map((digit, i) => (
                <div
                  key={i}
                  className="w-14 h-16 bg-muted rounded-xl flex items-center justify-center text-3xl font-bold text-foreground"
                >
                  {digit}
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-muted-foreground mt-4">
              Use this code to lock/unlock the pod
            </p>
          </motion.div>
        )}

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-3"
        >
          <a
            href="https://chat.whatsapp.com/Lx9H5OJpywmCa022EcliOC"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full py-4 px-6 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-semibold flex items-center justify-center gap-3 transition-colors"
          >
            <MessageCircle className="w-5 h-5" />
            WhatsApp Support
          </a>

          <button
            onClick={handleEndSession}
            disabled={isSessionEnded}
            className={`w-full py-4 px-6 rounded-xl font-semibold flex items-center justify-center gap-3 transition-colors ${
              isSessionEnded
                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                : 'bg-destructive hover:bg-destructive/90 text-white'
            }`}
          >
            <LogOut className="w-5 h-5" />
            {isSessionEnded ? 'Session Ended' : 'End Session'}
          </button>
        </motion.div>

        {/* Help Text */}
        <p className="text-center text-sm text-muted-foreground">
          Having trouble? Contact support and we'll help you right away.
        </p>
      </div>

      {/* End Session Modal */}
      <EndSessionModal
        isOpen={showEndSessionModal}
        onClose={() => setShowEndSessionModal(false)}
        sessionToken={sessionToken}
        sessionId={sessionId}
      />
    </div>
  );
}
